import { useCurationsTree } from '../../hooks/useCurationsTree';
import type { CatalogCurationDictionaryEntry } from '../../state/Catalog';
import { useCatalogContext } from '../../state/Catalog';
import { useCatalogRegistryTranslations } from '../../Catalog.i18n';
import { useLocation } from '@react-router';
import { useCatalogRouting } from '../../useCatalogRouting';
import { useEffect } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCatalogProductListData } from '../../hooks/useCatalogProductListData/useCatalogProductListData';
import { CATALOG_PRODUCT_LIST_PAGE_SIZE } from '../../Catalog.constants';
import { useCurationDetailsHealth } from '@apps/registry/common/components/Catalog/routes/CurationDetails/CurationDetails.health';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import { useTranslation } from '@shared/core';

const usePageTelemetry = ({ selectedCuration }: { selectedCuration: Maybe<CatalogCurationDictionaryEntry> }) => {
  const { curationDetail: trackCurationDetailPage, getProductCatalogCategoryBasePageEventProps } = useCatalogLoggingContext();

  const trackPage = useEventCallback((action: 'enter') => {
    const categoryPageProps = getProductCatalogCategoryBasePageEventProps({
      product: null,
      curation: selectedCuration,
    }).categoryTelemArgs;

    trackCurationDetailPage[action]?.(categoryPageProps);
  });

  useEffect(() => {
    trackPage('enter');

    return () => {};
  }, [trackPage]);
};

export const useCurationDetailsController = ({ isMobile }: { isMobile: boolean }) => {
  const { getPublicPathFromContextualUrl } = useCatalogRouting();
  const { curationsDictionary, isLoadingTreeRootData, error: curationsTreeError } = useCurationsTree();
  const location = useLocation();
  const { getAddProductTranslations } = useCatalogRegistryTranslations();
  const { notificationText } = getAddProductTranslations();
  const { notificationVisible, setNotificationVisible, redirectPath } = useCatalogContext();
  const { t } = useTranslation('catalogRegistry');
  const { currentStorefront } = useShopStorefrontContext();
  const seoTrans = t('seo', currentStorefront);

  const {
    loading: isLoadingSearchResults,
    productList,
    filters,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage,
    isFetchingMore,
    error,
  } = useCatalogProductListData({
    pageSize: CATALOG_PRODUCT_LIST_PAGE_SIZE,
    selectedCategory: null,
    telemetryPageValue: 'curationDetail',
    searchTerm: null,
  });

  const { onLoadComplete, onLoadFailure } = useCurationDetailsHealth();
  useEffect(() => {
    if (curationsTreeError) {
      onLoadFailure('CatalogCurationsTreeRootQuery failed', error);
    } else if (error) {
      onLoadFailure('GetAllCatalogProductGridItemsQuery failed', error);
    } else if (productList) {
      onLoadComplete();
    }
  }, [curationsTreeError, error, onLoadFailure, productList, onLoadComplete]);

  const url = getPublicPathFromContextualUrl(location.pathname);
  const selectedCuration = curationsDictionary[url];

  usePageTelemetry({ selectedCuration });

  const { value: shopProductConfirmationToastV2Enabled } = useFeatureValue('shopProductConfirmationToastV2');

  return {
    seoTrans,
    filters,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage,
    isFetchingMore,
    isLoadingTreeRootData,
    isLoadingSearchResults,
    isMobile,
    notificationText,
    notificationVisible,
    setNotificationVisible,
    selectedCuration,
    searchResults: productList,
    shopProductConfirmationToastV2Enabled: shopProductConfirmationToastV2Enabled === 'on',
    redirectPath,
  };
};
